import { useCallback, useMemo, useState } from "react";
import {
  Paper,
  Stack,
  Group,
  Text,
  Badge,
  UnstyledButton,
} from "@mantine/core";
import { IconPrinter } from "@tabler/icons-react";
import { useBridgeStatus } from "../../hooks/useBridgeStatus";
import { useWorkstationDeviceStatus } from "../../hooks/useWorkstationDeviceStatus";
import { WorkstationSelectorModal } from "../bridge";
import { workstationPreferenceService } from "../../services/workstationPreference.service";

interface SlidePrinterStatusPanelProps {
  title?: string;
}

/**
 * Printer status panel for slide label printing via Bridge.
 * Checks real backend workstation + device connection status.
 * Clicking opens the workstation selector if not configured or offline.
 */
export default function SlidePrinterStatusPanel({
  title = "Slide Printer",
}: SlidePrinterStatusPanelProps) {
  const [selectorOpen, setSelectorOpen] = useState(false);
  // Keep bridge WebSocket alive while this panel is mounted so
  // the local bridge connection persists across page navigations.
  useBridgeStatus();
  const status = useWorkstationDeviceStatus("slide_printer");
  const preferredPrinter =
    workstationPreferenceService.getPreferredPrinter("slide_printer");
  const preferredRoute = useMemo(
    () =>
      preferredPrinter &&
      workstationPreferenceService.shouldUseBackendForDevice(
        "slide_printer",
        preferredPrinter.deviceId
      )
        ? "via backend"
        : "local-first",
    [preferredPrinter]
  );

  const badgeProps = useMemo(() => {
    if (preferredPrinter) {
      return { color: "blue", children: "Preferred" };
    }
    if (!status.hasWorkstation) {
      return { color: "orange", children: "Not Configured" };
    }
    if (status.isLoading) {
      return { color: "gray", children: "Checking…" };
    }
    if (!status.workstationOnline) {
      return { color: "red", children: "Offline" };
    }
    if (!status.device) {
      return { color: "orange", children: "No Device" };
    }
    if (!status.deviceConnected) {
      return { color: "red", children: "Disconnected" };
    }
    return { color: "green", children: "Ready" };
  }, [
    preferredPrinter,
    status.device,
    status.deviceConnected,
    status.hasWorkstation,
    status.isLoading,
    status.workstationOnline,
  ]);

  const statusMessage = useMemo(() => {
    if (preferredPrinter) {
      return `${preferredPrinter.deviceName} on ${preferredPrinter.workstationName} — ${preferredRoute}; readiness not verified`;
    }
    if (!status.hasWorkstation) {
      return "Click to configure your workstation";
    }
    if (status.isLoading) {
      return "Checking connection status…";
    }
    if (!status.workstationOnline) {
      return `${status.workstationName || "Workstation"} is offline`;
    }
    if (!status.device) {
      return "No slide printer on this workstation";
    }
    if (!status.deviceConnected) {
      return `${status.device.name} is disconnected`;
    }
    return `${status.device.name} on ${status.workstationName}`;
  }, [
    preferredPrinter,
    preferredRoute,
    status.device,
    status.deviceConnected,
    status.hasWorkstation,
    status.isLoading,
    status.workstationName,
    status.workstationOnline,
  ]);

  const handleOpen = useCallback(() => {
    setSelectorOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setSelectorOpen(false);
  }, []);

  return (
    <>
      <UnstyledButton
        onClick={handleOpen}
        style={{ cursor: "pointer", width: "100%" }}
      >
        <Paper p="md" withBorder>
          <Stack gap="xs">
            <Group justify="space-between" align="center">
              <Group gap="xs">
                <IconPrinter size={16} />
                <Text fw={500} size="sm">
                  {title}
                </Text>
              </Group>
              <Badge size="xs" color={badgeProps.color}>
                {badgeProps.children}
              </Badge>
            </Group>
            <Text size="xs" c="dimmed">
              {statusMessage}
            </Text>
          </Stack>
        </Paper>
      </UnstyledButton>

      <WorkstationSelectorModal opened={selectorOpen} onClose={handleClose} />
    </>
  );
}
